import { useState, useEffect, useRef } from 'react'
import { InputMask } from '@react-input/mask'
import Select, { createFilter } from 'react-select'
import CustomOption from '../../ReactSelect/CustomOption'
import CustomMenuList from '../../ReactSelect/CustomMenuList'
import '../../ReactSelect/reactSelect.css'
import './ColumnHeaderFilter.css'

export const ColumnHeaderFilter = ({
    title,
    filterKey,
    value,
    onChange,
    className,
    style,
}) => {
    const [localValue, setLocalValue] = useState(value || '')
    const debounceRef = useRef(null)

    useEffect(() => {
        setLocalValue(value || '')
    }, [value])

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const handleChange = (e) => {
        const newValue = e.target.value
        setLocalValue(newValue)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            onChange(filterKey, newValue)
        }, 1000)
    }

    return (
        <div className="column-header-filter">
            <span
                style={style}
                className={`column-header-filter__title ${className || ''}`}
                title={title}
            >
                {title}
            </span>
            <div className="column-header-filter__input-wrapper">
                <input
                    type="text"
                    className={`column-header-filter__input }`}
                    placeholder="Search..."
                    value={localValue}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={handleChange}
                />
            </div>
        </div>
    )
}

/** Phone filter - mask pattern (123)456-7890, no validation */
export const ColumnHeaderPhoneFilter = ({
    title,
    filterKey,
    value,
    onChange,
     className,
    style,
}) => {
    const [localValue, setLocalValue] = useState(value || '')
    const debounceRef = useRef(null)

    useEffect(() => {
        setLocalValue(value || '')
    }, [value])

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const handleChange = (e) => {
        const newValue = e?.target?.value || ''
        setLocalValue(newValue)

        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            onChange(filterKey, newValue.trim())
        }, 1000)
    }

    return (
        <div className="column-header-filter">
           <span
                style={style}
                className={`column-header-filter__title ${className || ''}`}
                title={title}
            >
                {title}</span>
            <div
                className="column-header-filter__phone-wrapper"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <InputMask
                    mask="(___)___-____"
                    replacement={{ _: /\d/ }}
                    value={localValue}
                    onChange={handleChange}
                    className="column-header-filter__phone-input"
                    placeholder="(123)456-7890"
                />
            </div>
        </div>
    )
}

export const ColumnHeaderSelectFilter = ({
    title,
    filterKey,
    value,
    onChange,
    options = [],
     className,
    style,
}) => {
    return (
        <div className="column-header-filter">
             <span
                style={style}
                className={`column-header-filter__title ${className || ''}`}
                title={title}
            >
                {title}</span>
            <select
                className="column-header-filter__select"
                value={value || ''}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => onChange(filterKey, e.target.value)}
            >
                <option value="">All</option>
                {options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    )
}

/** Uses react-select (same as users filter modal) for User Type - searchable, same look & options */
export const ColumnHeaderReactSelectFilter = ({
    title,
    filterKey,
    value,
    onChange,
    options = [],
    className,
    style,
    menuMinWidth
}) => {
    const selectedOption =
        options?.find((opt) => String(opt.value) === String(value)) || null

    return (
        <div className="column-header-filter">
            <span 
             style={style}
              title={title}
            className={`column-header-filter__title ${className || ''}`}>{title}</span>
            <div
                className="column-header-filter__react-select"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <Select
                    value={selectedOption}
                    onChange={(opt) => onChange(filterKey, opt?.value ?? '')}
                    options={options}
                    isClearable
                    placeholder="All"
                    classNamePrefix="react-select"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    filterOption={createFilter({ ignoreAccents: false })}
                    components={{
                        Option: CustomOption,
                        MenuList: CustomMenuList,
                    }}
                    styles={{
                        control: (base) => ({
                            ...base,
                            minHeight: 38,
                            fontSize: 13,
                        }),
                        valueContainer: (base) => ({
                            ...base,
                            padding: '0 8px 0 10px',
                        }),
                        menu: (base) => ({
                            ...base,
                            backgroundColor: '#ffffff',
                            zIndex: 9999,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }),
                        menuPortal: (base) => {
                            if (!menuMinWidth) return { ...base, zIndex: 9999 }
                            // Optional: let the menu be wider than a narrow column
                            const menuWidth = Math.max(base.width, menuMinWidth)
                            // Near the right edge (e.g. last column): open leftwards so the list and its scrollbar stay on screen
                            const overflowsRight = base.left + menuWidth > document.documentElement.clientWidth
                            return {
                                ...base,
                                zIndex: 9999,
                                width: menuWidth,
                                ...(overflowsRight ? { left: base.left + base.width - menuWidth } : {}),
                            }
                        },
                        option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                                ? 'var(--azureColor, #007bff)'
                                : state.isFocused
                                  ? '#f1f3f5'
                                  : '#ffffff',
                            color: state.isSelected ? '#ffffff' : '#333333',
                        }),
                    }}
                />
            </div>
        </div>
    )
}

export const ColumnHeaderDateRangeFilter = ({
    title,
    startFilterKey,
    endFilterKey,
    startValue,
    endValue,
    onChange,
    syncSingleDate = false,
    singleInput = false,
    className,
    style,
}) => {
    const [localStart, setLocalStart] = useState(startValue || '')
    const [localEnd, setLocalEnd] = useState(endValue || '')
    const startDebounceRef = useRef(null)
    const endDebounceRef = useRef(null)
    const bothDebounceRef = useRef(null)

    useEffect(() => {
        setLocalStart(startValue || '')
    }, [startValue])

    useEffect(() => {
        setLocalEnd(endValue || '')
    }, [endValue])

    useEffect(() => {
        return () => {
            if (startDebounceRef.current) clearTimeout(startDebounceRef.current)
            if (endDebounceRef.current) clearTimeout(endDebounceRef.current)
            if (bothDebounceRef.current) clearTimeout(bothDebounceRef.current)
        }
    }, [])

    const clearBothDebounced = () => {
        if (bothDebounceRef.current) {
            clearTimeout(bothDebounceRef.current)
            bothDebounceRef.current = null
        }
    }

    const clearStartEndDebounced = () => {
        if (startDebounceRef.current) {
            clearTimeout(startDebounceRef.current)
            startDebounceRef.current = null
        }
        if (endDebounceRef.current) {
            clearTimeout(endDebounceRef.current)
            endDebounceRef.current = null
        }
    }

    const scheduleBoth = (value) => {
        clearStartEndDebounced()
        clearBothDebounced()
        bothDebounceRef.current = setTimeout(() => {
            bothDebounceRef.current = null
            onChange(startFilterKey, value)
            onChange(endFilterKey, value)
        }, 300)
    }

    const scheduleChange = (key, value) => {
        clearBothDebounced()
        const ref =
            key === startFilterKey ? startDebounceRef : endDebounceRef

        if (ref.current) clearTimeout(ref.current)
        ref.current = setTimeout(() => {
            onChange(key, value)
        }, 300)
    }

    return (
        <div className="column-header-filter">
            <span
                style={style}
                className={`column-header-filter__title ${className || ''}`}
                title={title}
            >
                {title}
            </span>
            <div className="column-header-filter__input-wrapper d-flex gap-1">
                <input
                    type="date"
                    className="column-header-filter__input"
                    value={localStart}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        const newValue = e.target.value
                        setLocalStart(newValue)
                        // When using a single date input, clearing must clear BOTH keys;
                        // otherwise the old end date can linger in parent state and keep filtering.
                        if (syncSingleDate && singleInput && (!newValue || !String(newValue).trim())) {
                            setLocalEnd('')
                            scheduleBoth('')
                            return
                        }
                        if (
                            syncSingleDate &&
                            newValue &&
                            String(newValue).trim() &&
                            (!localEnd || !String(localEnd).trim())
                        ) {
                            setLocalEnd(newValue)
                            scheduleBoth(newValue)
                        } else {
                            scheduleChange(startFilterKey, newValue)
                        }
                    }}
                />
                {!singleInput && (
                    <input
                        type="date"
                        className="column-header-filter__input"
                        value={localEnd}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onChange={(e) => {
                            const newValue = e.target.value
                            setLocalEnd(newValue)
                        if (syncSingleDate && (!newValue || !String(newValue).trim())) {
                            setLocalStart('')
                            scheduleBoth('')
                            return
                        }
                            if (
                                syncSingleDate &&
                                newValue &&
                                String(newValue).trim() &&
                                (!localStart || !String(localStart).trim())
                            ) {
                                setLocalStart(newValue)
                                scheduleBoth(newValue)
                            } else {
                                scheduleChange(endFilterKey, newValue)
                            }
                        }}
                    />
                )}
            </div>
        </div>
    )
}
